const STORAGE_KEY = 'fountain:auth:intended_destination';
const MAX_AGE_MS = 30 * 60 * 1000;

interface StoredDestination {
  url: string;
  savedAt: number;
}

function isStoredDestination(value: unknown): value is StoredDestination {
  return (
    typeof value === 'object' &&
    value !== null &&
    'url' in value &&
    'savedAt' in value &&
    typeof (value as StoredDestination).url === 'string' &&
    typeof (value as StoredDestination).savedAt === 'number'
  );
}

function isSessionStorageAvailable(): boolean {
  try {
    const key = '__test__';
    sessionStorage.setItem(key, key);
    sessionStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

function isSameOrigin(url: string): boolean {
  try {
    if (url.startsWith('/') && !url.startsWith('//')) return true;
    return (
      new URL(url, window.location.origin).origin === window.location.origin
    );
  } catch {
    return false;
  }
}

export function saveIntendedDestination(url = window.location.href): boolean {
  if (!isSessionStorageAvailable() || !isSameOrigin(url)) return false;

  try {
    const data: StoredDestination = { url, savedAt: Date.now() };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    return true;
  } catch {
    return false;
  }
}

export function getAndClearIntendedDestination(): string | null {
  if (!isSessionStorageAvailable()) return null;

  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);

    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (!isStoredDestination(parsed)) return null;

    const { url, savedAt } = parsed;
    const isExpired = Date.now() - savedAt > MAX_AGE_MS;
    if (isExpired || !isSameOrigin(url)) return null;

    return url;
  } catch {
    return null;
  }
}
